import { useMemo, useRef, useState } from 'react';
import { Box, Skeleton, Typography } from '@mui/material';
import Chart, { ArgumentAxis, Label, Legend, Series, Tooltip, ValueAxis } from 'devextreme-react/chart';
import PieChart, {
    Connector,
    Label as PieLabel,
    Legend as PieLegend,
    Series as PieSeries,
    Tooltip as PieTooltip,
} from 'devextreme-react/pie-chart';
import { EnrollmentByAdvisorMonthlyDistributionDto } from 'generated/openApi';
import { DashboardCard } from '../components/DashboardCard';
import { DashboardChartViewSelect } from '../components/DashboardChartViewSelect';
import { useEnrollmentByAdvisorData } from '../contexts';
import { useDashboardChartView } from '../contexts/DashboardChartViewContext';
import {
    DASHBOARD_CHART_VIEW_CONFIG,
    DASHBOARD_DISTRIBUTION_COLORS,
    DashboardChartView,
    getDashboardDistributionColor,
} from '../types';
import { EnrollmentByAdvisorControls, EnrollmentPeriod } from './EnrollmentByAdvisorControls';

export const EnrollmentByAdvisorChartCard = () => {
    const { data, monthlyData: monthlyDistributionData, loading, error, filter } = useEnrollmentByAdvisorData();
    const monthlyData = monthlyDistributionData as EnrollmentByAdvisorMonthlyDistributionDto[];
    const [period, setPeriod] = useState<EnrollmentPeriod>('year');
    const [month, setMonth] = useState<number | null>(null);
    const [chartView, setChartView] = useDashboardChartView('enrollmentByAdvisor');
    const chartRef = useRef<Chart>(null);
    const pieChartRef = useRef<PieChart>(null);
    const rows = useMemo(() => {
        const advisors = new Map<number, { advisor: string; studentCount: number }>();
        data.forEach(item => {
            const row = advisors.get(item.enrollmentByAdvisorId);
            if (row) row.studentCount += item.studentCount;
            else advisors.set(item.enrollmentByAdvisorId, {
                advisor: item.enrollmentByAdvisorName,
                studentCount: item.studentCount,
            });
        });
        return Array.from(advisors.values()).sort((a, b) => b.studentCount - a.studentCount || a.advisor.localeCompare(b.advisor));
    }, [data]);
    const monthlyAdvisors = useMemo(() => {
        const advisors = new Map<number, string>();
        monthlyData.filter(item => month === null || item.month === month)
            .forEach(item => advisors.set(item.enrollmentByAdvisorId, item.enrollmentByAdvisorName));
        return Array.from(advisors, ([id, name]) => ({ id, name }))
            .sort((left, right) => left.name.localeCompare(right.name));
    }, [monthlyData, month]);
    const monthlyRows = useMemo(() => {
        const months = new Map<string, Record<string, string | number>>();
        monthlyData.filter(item => month === null || item.month === month).forEach(item => {
            const key = `${item.startYear}-${String(item.month).padStart(2, '0')}`;
            const row = months.get(key) || {
                key,
                month: new Date(item.startYear, item.month - 1).toLocaleString(undefined, {
                    month: 'short',
                    year: 'numeric',
                }),
            };
            const advisorField = `advisor${item.enrollmentByAdvisorId}`;
            row[advisorField] = Number(row[advisorField] || 0) + item.studentCount;
            months.set(key, row);
        });
        return Array.from(months.values()).sort((left, right) => String(left.key).localeCompare(String(right.key)));
    }, [monthlyData, month]);
    const monthlyAdvisorRows = useMemo(() => {
        const advisors = new Map<number, { advisor: string; studentCount: number }>();
        monthlyData.filter(item => month === null || item.month === month).forEach(item => {
            const row = advisors.get(item.enrollmentByAdvisorId);
            if (row) row.studentCount += item.studentCount;
            else advisors.set(item.enrollmentByAdvisorId, {
                advisor: item.enrollmentByAdvisorName,
                studentCount: item.studentCount,
            });
        });
        return Array.from(advisors.values())
            .sort((left, right) => right.studentCount - left.studentCount || left.advisor.localeCompare(right.advisor));
    }, [monthlyData, month]);
    const hasData = period === 'month' ? monthlyRows.length > 0 : rows.length > 0;
    const pieRows = period === 'month' ? monthlyAdvisorRows : rows;
    const exportName = `Enrollment_by_Advisor_${filter.schoolYears.join('_') || 'CurrentYear'}`;
    const handleExport = () => {
        if (chartView === 'pie' || chartView === 'fullPie') {
            pieChartRef.current?.instance.exportTo(exportName, 'png');
        } else {
            chartRef.current?.instance.exportTo(exportName, 'png');
        }
    };

    return <DashboardCard
        title="Enrollment by Advisor"
        onExport={handleExport}
        rightActions={<DashboardChartViewSelect
            dashboard="enrollmentByAdvisor"
            value={chartView}
            onChange={setChartView}
            iconOnly
        />}
    >
        <Box sx={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
            <EnrollmentByAdvisorControls
                period={period}
                onPeriodChange={setPeriod}
                month={month}
                onMonthChange={setMonth}
            />
            {loading ? <Skeleton variant="rounded" height={400} /> : error ?
                <Typography color="error" align="center">{error}</Typography> : !hasData ?
                <Typography align="center">No enrollment-by-advisor data available</Typography> :
                <Box sx={{ flexGrow: 1, minHeight: 0 }}>
                    {chartView === 'pie' || chartView === 'fullPie' ? <PieChart
                        ref={pieChartRef}
                        dataSource={pieRows}
                        height="100%"
                        type={chartView === 'fullPie' ? 'pie' : 'doughnut'}
                        palette={DASHBOARD_DISTRIBUTION_COLORS}
                    >
                        <PieSeries argumentField="advisor" valueField="studentCount">
                            <PieLabel visible position="outside"><Connector visible /></PieLabel>
                        </PieSeries>
                        <PieLegend visible />
                        <PieTooltip enabled customizeTooltip={({ argumentText, valueText }) => ({
                            text: `${argumentText}: ${valueText} students`,
                        })} />
                    </PieChart> : <Chart
                        ref={chartRef}
                        dataSource={period === 'month' ? monthlyRows : rows}
                        height="100%"
                        rotated={chartView === 'horizontalBar'}
                        palette={DASHBOARD_DISTRIBUTION_COLORS}
                        customizePoint={period === 'month' ? undefined : (args: any) => ({ color: getDashboardDistributionColor(args.index) })}
                    >
                        <ArgumentAxis argumentType="string" />
                        <ValueAxis allowDecimals={false} />
                        {period === 'month' ? monthlyAdvisors.map((advisor, index) => <Series
                            key={advisor.id}
                            type="bar"
                            argumentField="month"
                            valueField={`advisor${advisor.id}`}
                            name={advisor.name}
                            color={getDashboardDistributionColor(index)}
                        />) : <Series type="bar" argumentField="advisor" valueField="studentCount" name="Students">
                            <Label visible />
                        </Series>}
                        <Legend visible={period === 'month'} />
                        <Tooltip enabled customizeTooltip={({ argumentText, valueText, seriesName }) => ({
                            text: period === 'month'
                                ? `${seriesName} — ${argumentText}: ${valueText} students`
                                : `${argumentText}: ${valueText} students`,
                        })} />
                    </Chart>}
                </Box>}
        </Box>
    </DashboardCard>;
};
